import React from "react";
import {
  Button,
  Card,
  Col,
  Row,
  Badge,
  Spinner,
  Modal,
} from "@themesberg/react-bootstrap";
import { useState, useCallback, useEffect } from "react";
import { postApi } from "../../../redux/api";
import CustomTable from "../../../components/CustomTable";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheckDouble,
  faEye,
  faSync,
  faUndo,
  faClipboardCheck,
} from "@fortawesome/free-solid-svg-icons";
import { formatNumber } from "../../../redux/utils";
import { useSelector } from "react-redux";
import moment from "moment";
import toast from "react-hot-toast";

function CashOfficeApprovePayments() {
  const user = useSelector((state) => state.auth.user);

  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [showDetail, setShowDetail] = useState(false);

  const fetchBatches = useCallback(async () => {
    try {
      setLoading(true);
      const data = await postApi("get_cash_office_batches", {
        form: { query_type: "reviewed" },
      });
      if (data?.result?.length) {
        setBatches(data.result);
      } else {
        setBatches([]);
      }
    } catch (err) {
      console.error("Error fetching approve batches:", err);
      setBatches([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBatches();
  }, [fetchBatches]);

  const viewDetail = (batch) => {
    setSelectedBatch(batch);
    setShowDetail(true);
  };

  const approvePayment = async (batchNumber) => {
    setProcessing(true);
    try {
      const data = await postApi("update_cash_office_batch", {
        batch_number: batchNumber,
        status: "paid",
        userId: user?.username,
      });
      if (data?.success) {
        toast.success(`Payment completed for batch ${batchNumber}!`);
        setShowDetail(false);
        setSelectedBatch(null);
        fetchBatches();
      } else {
        toast.error(data?.message || "Failed to process payment");
      }
    } catch (err) {
      toast.error("Failed to process payment. Please try again.");
      console.error(err);
    } finally {
      setProcessing(false);
    }
  };

  const returnBatch = async (batchNumber) => {
    setProcessing(true);
    try {
      const data = await postApi("update_cash_office_batch", {
        batch_number: batchNumber,
        status: "returned",
        userId: user?.username,
      });
      if (data?.success) {
        toast.success(`Batch ${batchNumber} returned.`);
        setShowDetail(false);
        setSelectedBatch(null);
        fetchBatches();
      } else {
        toast.error(data?.message || "Failed to return batch");
      }
    } catch (err) {
      toast.error("Failed to return batch. Please try again.");
      console.error(err);
    } finally {
      setProcessing(false);
    }
  };

  const batchFields = [
    { title: "Batch No", value: "batch_number" },
    {
      title: "No. of PVs",
      custom: true,
      component: (item) => (
        <span className="text-center d-block">{item.no_of_payments}</span>
      ),
    },
    {
      title: "Total Amount (₦)",
      tableHeadStyle: { textAlign: "right" },
      custom: true,
      component: (item) => (
        <div className="text-end">{formatNumber(item.total_amount)}</div>
      ),
    },
    {
      title: "Created Date",
      custom: true,
      component: (item) => (
        <span>{moment(item.created_date).format("DD/MM/YYYY")}</span>
      ),
    },
    {
      title: "Action",
      custom: true,
      component: (item) => (
        <Button
          size="sm"
          variant="outline-primary"
          onClick={() => viewDetail(item)}
        >
          <FontAwesomeIcon icon={faEye} className="me-1" />
          View & Approve
        </Button>
      ),
    },
  ];

  const pvDetailFields = [
    { title: "PV Code", value: "pv_code" },
    { title: "MDA Name", value: "mda_name" },
    {
      title: "Amount (₦)",
      tableHeadStyle: { textAlign: "right" },
      custom: true,
      component: (item) => (
        <div className="text-end">{formatNumber(item.amount)}</div>
      ),
    },
    {
      title: "Contractor",
      value: "contractor_name",
    },
  ];

  return (
    <>
      <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center py-4">
        <div>
          <h4>
            <FontAwesomeIcon icon={faClipboardCheck} className="me-2" />
            Approve Payments — Cash Office
          </h4>
        </div>
        <Button variant="outline-primary" size="sm" onClick={fetchBatches}>
          <FontAwesomeIcon icon={faSync} className="me-1" />
          Refresh
        </Button>
      </div>

      <Card className="mb-4">
        <Card.Body>
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <p className="mt-2">Loading batches for approval...</p>
            </div>
          ) : batches.length === 0 ? (
            <div className="text-center py-5 text-muted">
              <FontAwesomeIcon icon={faClipboardCheck} size="3x" className="mb-3" />
              <h5>No batches pending approval</h5>
              <p>All reviewed batches have been approved or none are ready yet.</p>
            </div>
          ) : (
            <CustomTable
              fields={batchFields}
              data={batches}
              noSort={false}
              hover={true}
            />
          )}
        </Card.Body>
      </Card>

      <Modal
        show={showDetail}
        onHide={() => { setShowDetail(false); setSelectedBatch(null); }}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            Batch: {selectedBatch?.batch_number}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedBatch && (
            <>
              <Row className="mb-3">
                <Col md={4}>
                  <strong>Batch No:</strong> {selectedBatch.batch_number}
                </Col>
                <Col md={4}>
                  <strong>No. of PVs:</strong> {selectedBatch.no_of_payments}
                </Col>
                <Col md={4}>
                  <strong>Total Amount:</strong> ₦
                  {formatNumber(selectedBatch.total_amount)}
                </Col>
              </Row>
              <Row className="mb-3">
                <Col md={4}>
                  <strong>Created:</strong>{" "}
                  {moment(selectedBatch.created_date).format("DD/MM/YYYY")}
                </Col>
                <Col md={4}>
                  <strong>Status:</strong>{" "}
                  <Badge bg="success">{selectedBatch.status}</Badge>
                </Col>
              </Row>
              <h6 className="mt-4 mb-2">PVs in this batch:</h6>
              <CustomTable
                fields={pvDetailFields}
                data={selectedBatch.pvs || []}
                noSort={false}
                hover={true}
              />
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="danger"
            onClick={() => returnBatch(selectedBatch?.batch_number)}
            disabled={processing}
          >
            {processing ? (
              <Spinner animation="border" size="sm" className="me-1" />
            ) : (
              <FontAwesomeIcon icon={faUndo} className="me-1" />
            )}
            Return Batch
          </Button>
          <Button
            variant="success"
            onClick={() => approvePayment(selectedBatch?.batch_number)}
            disabled={processing}
          >
            {processing ? (
              <Spinner animation="border" size="sm" className="me-1" />
            ) : (
              <FontAwesomeIcon icon={faCheckDouble} className="me-1" />
            )}
            Approve & Make Payment
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default CashOfficeApprovePayments;
